import { useState } from 'react'
import type { PantryItem } from '../../shared/types.ts'
import { api } from '../lib/api.ts'
import { X } from 'lucide-react'
import { amountText, cx, expiryLabel } from '../lib/util.ts'
import { Sheet } from './Sheet.tsx'

/**
 * Optional: food you want tracked between conversations, with expiry.
 * Anything in here is appended to every request, and recipes get checked against it.
 */
export function PantrySheet({ pantry, onPantry, onClose, canType }: {
  pantry: PantryItem[]
  onPantry: (p: PantryItem[]) => void
  onClose: () => void
  /** Free-text parsing needs Inception. */
  canType: boolean
}) {
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const add = async () => {
    const text = draft.trim()
    if (!text) return
    setBusy(true); setError('')
    try {
      onPantry((await api.parseItems(text)).pantry)
      setDraft('')
    } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }

  const sorted = [...pantry].sort((a, b) => {
    const ea = expiryLabel(a.expiresOn), eb = expiryLabel(b.expiresOn)
    if (ea && !eb) return -1
    if (!ea && eb) return 1
    return a.name.localeCompare(b.name)
  })

  return (
    <Sheet title="What's in the house" onClose={onClose}>
      <p className="muted">
        Optional. Anything listed here is sent with every request, and each recipe gets checked
        against it — so you see what you already have and what you'd need to buy.
      </p>

      <div className="pantry-add">
        <textarea
          value={draft}
          className="input"
          rows={2}
          disabled={!canType}
          placeholder={canType ? '600g chicken thighs going off tomorrow, half a cabbage, jar of gochujang…' : 'Needs INCEPTION_API_KEY'}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void add() } }}
        />
        <button className="btn btn-primary" onClick={() => void add()} disabled={busy || !draft.trim() || !canType}>
          {busy ? 'Reading…' : 'Add'}
        </button>
      </div>
      {error && <div className="notice error">{error}</div>}

      {pantry.length === 0 ? (
        <p className="muted small">Nothing tracked. You can always just say what you have in the chat instead.</p>
      ) : (
        <>
          <div className="item-list">
            {sorted.map((item) => {
              const exp = expiryLabel(item.expiresOn)
              return (
                <div key={item.id} className={cx('item', exp?.level === 'expired' && 'expired', exp?.level === 'urgent' && 'urgent')}>
                  <div className="item-main">
                    <div className="item-name">{item.name}</div>
                    {(exp || item.notes) && (
                      <div className="item-meta">
                        {exp && <span>{exp.level === 'expired' ? `expired ${exp.text.replace('expired ', '')}` : `use within ${exp.text}`}</span>}
                        {item.notes && <span>{item.notes}</span>}
                      </div>
                    )}
                  </div>
                  <span className="item-qty">{amountText(item)}</span>
                  <button
                    className="x"
                    aria-label={`Remove ${item.name}`}
                    onClick={async () => onPantry((await api.removeItem(item.id)).pantry)}
                  ><X size={17} strokeWidth={2.6} /></button>
                </div>
              )
            })}
          </div>
          <button
            className="btn btn-secondary"
            style={{ marginTop: 14 }}
            onClick={async () => { if (confirm(`Remove all ${pantry.length} items?`)) onPantry((await api.clearPantry()).pantry) }}
          >
            Clear everything
          </button>
        </>
      )}
    </Sheet>
  )
}
